/**
 * Frontend TypeScript interfaces for handling API errors
 * Adapted for axios usage in React application
 */

export type BackendSource = 'dotnet' | 'puppeteer' | 'unknown';

export interface IApiError {
  message: string;
  stack: string | object;
  url: string; // Full URL including base path
  statusCode: number;
  timestamp: string;
  _blob?: Blob;
  correlationId?: string;
  backendSource?: BackendSource; // Which backend the error came from
}

/**
 * Type guard to check if a response is an API error
 * Handles both camelCase (from .NET backend) and PascalCase formats
 */
export function isApiError(obj: any): obj is IApiError {
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  // Check for required fields (handle both camelCase and PascalCase)
  // Use explicit property checks so valid falsy values (e.g. statusCode 0, empty url) are preserved.
  const messageValue = obj.message ?? obj.Message;
  const statusCodeValue = obj.statusCode ?? obj.StatusCode ?? obj.status ?? obj.Status;
  const urlValue = obj.url ?? obj.Url;
  const hasMessage = typeof messageValue === 'string';
  const hasStatusCode = typeof statusCodeValue === 'number';
  const hasUrl = typeof urlValue === 'string';

  // Timestamp is optional (we can generate it if missing)
  // But if present, it should be a string
  const hasValidTimestamp = (!obj.timestamp && !obj.Timestamp) ||
    typeof (obj.timestamp || obj.Timestamp) === 'string';

  return hasMessage && hasStatusCode && hasUrl && hasValidTimestamp;
}

/**
 * Detects aborted/cancelled HTTP requests (axios/fetch aborts).
 * These are expected control-flow events and should not be shown as user-facing errors.
 */
export function isCancelledRequestError(error: any): boolean {
  if (!error) {
    return false;
  }

  const errorCode = typeof error.code === 'string' ? error.code.toUpperCase() : '';
  const errorName = typeof error.name === 'string' ? error.name.toLowerCase() : '';
  const errorMessage = typeof error.message === 'string' ? error.message.toLowerCase() : '';

  return (
    errorCode === 'ERR_CANCELED' ||
    errorName === 'cancelederror' ||
    errorName === 'aborterror' ||
    errorMessage === 'canceled' ||
    errorMessage === 'cancelled' ||
    errorMessage.includes('aborted')
  );
}

/**
 * Utility class for handling API errors in the frontend with axios
 */
export class ApiErrorHandler {
  /**
   * Determines whether an error is a cancelled/aborted request.
   */
  static isCancelledRequest(error: any): boolean {
    return isCancelledRequestError(error);
  }

  /**
   * Parses an axios error response and converts it to IApiError format
   * @param error - The axios error object
   * @param url - The URL that was being requested
   * @returns IApiError - The standardized error object
   */
  static parseAxiosError(error: any, url: string = ''): IApiError {
    // Helper function to extract correlation ID from multiple sources
    const getCorrelationId = (responseData?: any): string | undefined => {
      // First try to get from response body (both .NET and Node.js backends include it)
      if (responseData?.correlationId) {
        return responseData.correlationId;
      }
      // Also check for PascalCase (in case backend uses it)
      if (responseData?.CorrelationId) {
        return responseData.CorrelationId;
      }
      // Then try response headers (backend might echo it back)
      if (error.response?.headers?.['x-correlation-id']) {
        return error.response.headers['x-correlation-id'];
      }
      // Finally try request headers (what we sent)
      if (error.config?.headers?.['x-correlation-id']) {
        return error.config.headers['x-correlation-id'];
      }
      return undefined;
    };

    // Helper function to normalize error data from backend (handle both camelCase and PascalCase)
    const normalizeErrorData = (data: any): any => {
      if (!data || typeof data !== 'object') {
        return data;
      }

      // If already in correct format, return as-is
      if (isApiError(data)) {
        return data;
      }

      // Normalize property names (handle both camelCase and PascalCase)
      return {
        message: data.message || data.Message || data.errorMessage || data.ErrorMessage || '',
        statusCode: data.statusCode || data.StatusCode || data.status || data.Status || 500,
        stack: data.stack || data.Stack || data.stackTrace || data.StackTrace || '',
        url: data.url || data.Url || '',
        timestamp: data.timestamp || data.Timestamp || new Date().toISOString(),
        correlationId: data.correlationId || data.CorrelationId
      };
    };

    // If the error response contains our standardized API error format
    if (error.response?.data) {
      const normalizedData = normalizeErrorData(error.response.data);

      // Check if normalized data matches our API error format
      if (normalizedData.message &&
        typeof normalizedData.statusCode === 'number' &&
        normalizedData.url) {
        return {
          message: normalizedData.message,
          stack: normalizedData.stack || '',
          url: normalizedData.url || url || error.config?.url || '',
          statusCode: normalizedData.statusCode,
          timestamp: normalizedData.timestamp || new Date().toISOString(),
          correlationId: normalizedData.correlationId || getCorrelationId(error.response.data)
        };
      }
    }

    // If the error response has data but not in our format
    if (error.response?.data) {
      // Handle Blob responses (common with PDF/file downloads)
      if (error.response.data instanceof Blob) {
        // For Blob errors, we need to read the content asynchronously
        // Since this method is synchronous, we'll return a promise-like structure
        // and handle the blob reading in the calling code
        return {
          message: 'Ett fel uppstod vid filhantering',
          stack: error.stack || '',
          url: url || error.config?.url || '',
          statusCode: error.response.status || 500,
          timestamp: new Date().toISOString(),
          _blob: error.response.data, // Store blob for potential async reading
          correlationId: getCorrelationId()
        } as IApiError;
      }

      // Try to extract the message from various possible formats
      let message = '';
      if (typeof error.response.data === 'string') {
        message = error.response.data;
      } else if (error.response.data.message || error.response.data.Message || error.response.data.errorMessage || error.response.data.ErrorMessage) {
        message = error.response.data.message || error.response.data.Message || error.response.data.errorMessage || error.response.data.ErrorMessage;
      } else if (error.response.data.error || error.response.data.Error) {
        message = error.response.data.error || error.response.data.Error;
      } else if (error.response.data.title || error.response.data.Title) {
        message = error.response.data.title || error.response.data.Title;
      } else {
        // Only use generic message as last resort
        message = 'Unknown error occurred';
      }

      // Extract status code (handle both camelCase and PascalCase)
      const statusCode = error.response.status ||
        error.response.data.statusCode ||
        error.response.data.StatusCode ||
        error.response.data.status ||
        error.response.data.Status ||
        500;

      return {
        message: message,
        stack: error.response.data.stack || error.response.data.Stack || error.response.data.stackTrace || error.response.data.StackTrace || '',
        url: error.response.data.url || error.response.data.Url || url || error.config?.url || '',
        statusCode: statusCode,
        timestamp: error.response.data.timestamp || error.response.data.Timestamp || new Date().toISOString(),
        correlationId: getCorrelationId(error.response.data)
      };
    }

    // Handle axios request errors (network issues, etc.)
    if (error.request) {
      return {
        message: error.message || 'Kunde inte ansluta till servern. Kontrollera din internetanslutning.',
        stack: error.stack || '',
        url: url || error.config?.url || '',
        statusCode: 0, // Network errors don't have HTTP status codes
        timestamp: new Date().toISOString(),
        correlationId: getCorrelationId()
      };
    }

    // Handle other errors (setup errors, etc.)
    return {
      message: error.message || 'Ett oväntat fel uppstod',
      stack: error.stack || '',
      url: url || '',
      statusCode: 500,
      timestamp: new Date().toISOString(),
      correlationId: getCorrelationId()
    };
  }

  /**
   * Async method to parse blob errors and extract the actual error message
   * @param error - The axios error object with blob data
   * @param url - The URL that was being requested
   * @returns Promise<IApiError> - The standardized error object with actual message
   */
  static async parseBlobError(error: any, url: string = ''): Promise<IApiError> {
    // Helper function to extract correlation ID from multiple sources
    const getCorrelationId = (responseData?: any): string | undefined => {
      // First try to get from response body (both .NET and Node.js backends include it)
      if (responseData?.correlationId) {
        return responseData.correlationId;
      }
      // Also check for PascalCase (in case backend uses it)
      if (responseData?.CorrelationId) {
        return responseData.CorrelationId;
      }
      // Then try response headers (backend might echo it back)
      if (error.response?.headers?.['x-correlation-id']) {
        return error.response.headers['x-correlation-id'];
      }
      // Finally try request headers (what we sent)
      if (error.config?.headers?.['x-correlation-id']) {
        return error.config.headers['x-correlation-id'];
      }
      return undefined;
    };

    if (error.response?.data instanceof Blob) {
      try {
        // Check Content-Type header to determine if it's JSON
        const contentType = error.response?.headers?.['content-type'] ||
          error.response?.headers?.['Content-Type'] || '';
        const isJsonContentType = contentType.includes('application/json') ||
          contentType.includes('text/json');

        const text = await error.response.data.text();
        let parsedData;

        // Clean the text - remove BOM and trim whitespace
        const cleanedText = text.trim().replace(/^\uFEFF/, '');

        // Check if it looks like JSON (starts with { or [) or has JSON content type
        const looksLikeJson = isJsonContentType ||
          cleanedText.startsWith('{') ||
          cleanedText.startsWith('[');

        if (looksLikeJson) {
          try {
            parsedData = JSON.parse(cleanedText);

            // Log successful parsing for debugging
            console.log('Successfully parsed JSON from blob error:', parsedData);

            // Successfully parsed JSON - extract fields (handle both camelCase and PascalCase)
            const message = parsedData.message ||
              parsedData.Message ||
              parsedData.error ||
              parsedData.Error ||
              parsedData.title ||
              parsedData.Title ||
              'Ett fel uppstod vid filhantering';

            // Extract status code
            const statusCode = error.response.status ||
              parsedData.statusCode ||
              parsedData.StatusCode ||
              parsedData.status ||
              parsedData.Status ||
              500;

            return {
              message: message,
              stack: parsedData.stack || parsedData.Stack || parsedData.stackTrace || parsedData.StackTrace || '',
              url: parsedData.url || parsedData.Url || url || error.config?.url || '',
              statusCode: statusCode,
              timestamp: parsedData.timestamp || parsedData.Timestamp || new Date().toISOString(),
              correlationId: parsedData.correlationId || parsedData.CorrelationId || getCorrelationId(parsedData)
            };
          } catch (parseError: any) {
            // Log the parse error for debugging
            console.error('Failed to parse JSON from blob error response:', {
              parseError: parseError?.message || parseError,
              textLength: cleanedText.length,
              textPreview: cleanedText.substring(0, 200),
              contentType: error.response?.headers?.['content-type'],
              fullText: cleanedText // Log full text in dev mode for debugging
            });

            // If JSON parsing fails, try to extract message from text anyway
            // Sometimes the text might be JSON-like but malformed
            // Use a more robust regex that handles escaped quotes
            const messageMatch = cleanedText.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
            const statusMatch = cleanedText.match(/"statusCode"\s*:\s*(\d+)/);
            const correlationMatch = cleanedText.match(/"correlationId"\s*:\s*"((?:[^"\\]|\\.)*)"/);
            const urlMatch = cleanedText.match(/"url"\s*:\s*"((?:[^"\\]|\\.)*)"/);
            const timestampMatch = cleanedText.match(/"timestamp"\s*:\s*"((?:[^"\\]|\\.)*)"/);

            if (messageMatch || statusMatch) {
              return {
                message: messageMatch?.[1]?.replace(/\\"/g, '"') || cleanedText.substring(0, 200) || 'Ett fel uppstod vid filhantering',
                stack: '',
                url: urlMatch?.[1]?.replace(/\\"/g, '"') || url || error.config?.url || '',
                statusCode: statusMatch ? parseInt(statusMatch[1], 10) : (error.response.status || 500),
                timestamp: timestampMatch?.[1]?.replace(/\\"/g, '"') || new Date().toISOString(),
                correlationId: correlationMatch?.[1]?.replace(/\\"/g, '"') || getCorrelationId()
              };
            }

            // Fallback: use the text as message
            return {
              message: cleanedText.substring(0, 500) || 'Ett fel uppstod vid filhantering',
              stack: '',
              url: url || error.config?.url || '',
              statusCode: error.response.status || 500,
              timestamp: new Date().toISOString(),
              correlationId: getCorrelationId()
            };
          }
        } else {
          // Not JSON, use text as message
          return {
            message: cleanedText || 'Ett fel uppstod vid filhantering',
            stack: '',
            url: url || error.config?.url || '',
            statusCode: error.response.status || 500,
            timestamp: new Date().toISOString(),
            correlationId: getCorrelationId()
          };
        }
      } catch (blobError) {
        console.error('Error reading blob:', blobError);
        return {
          message: 'Ett fel uppstod vid filhantering',
          stack: '',
          url: url || error.config?.url || '',
          statusCode: error.response.status || 500,
          timestamp: new Date().toISOString(),
          correlationId: getCorrelationId()
        };
      }
    }

    // Fallback to regular parsing if not a blob
    return this.parseAxiosError(error, url);
  }

  /**
   * Gets a user-friendly error message based on status code
   * @param apiError - The API error object
   * @returns string - User-friendly error message in Swedish
   */
  static getUserFriendlyMessage(apiError: IApiError): string {
    // Always prioritize the backend message if it exists and is meaningful
    if (apiError.message &&
      apiError.message !== 'Unknown error occurred' &&
      apiError.message !== 'Internal server error' &&
      apiError.message !== 'Bad Request' &&
      apiError.message !== 'Unauthorized' &&
      apiError.message !== 'Forbidden' &&
      apiError.message !== 'Not Found' &&
      apiError.message !== 'Network Error') {
      return apiError.message;
    }

    // Only use user-friendly messages for generic backend messages or network errors
    switch (apiError.statusCode) {
      case 400:
        return apiError.message || 'Felaktig förfrågan';
      case 401:
        return apiError.message || 'Du är inte behörig att utföra denna åtgärd. Logga in igen.';
      case 403:
        return apiError.message || 'Du har inte tillräckliga rättigheter för denna åtgärd.';
      case 404:
        return apiError.message || 'Den begärda resursen kunde inte hittas.';
      case 409:
        return apiError.message || 'Konflikt uppstod';
      case 422:
        return apiError.message || 'Valideringsfel';
      case 429:
        return 'För många förfrågningar. Försök igen senare.';
      case 500:
        return apiError.message || 'Ett serverfel uppstod. Kontakta supporten.';
      case 502:
        return 'Servern är tillfälligt otillgänglig. Försök igen senare.';
      case 503:
        return 'Tjänsten är tillfälligt otillgänglig. Försök igen senare.';
      case 0:
        return "Nätverksfel"; // Network errors already have Swedish message
      default:
        return apiError.message || 'Ett oväntat fel uppstod';
    }
  }

  /**
   * Determines if an error should be retried automatically
   * @param apiError - The API error object
   * @returns boolean - Whether the error is retryable
   */
  static isRetryableError(apiError: IApiError): boolean {
    return [0, 408, 429, 502, 503, 504].includes(apiError.statusCode);
  }

  /**
   * Determines if an error should redirect to login
   * @param apiError - The API error object
   * @returns boolean - Whether to redirect to login
   */
  static shouldRedirectToLogin(apiError: IApiError): boolean {
    return apiError.statusCode === 401;
  }
} 